import * as Enumerable from 'linq';
import { StatusFilterValue } from '../entities/job/job.filter.types';
import { SystemWorkOrderStatus } from '../entities/job/system.workorder.status';
import { WorkOrderStatus } from '../entities/job/workorder.status';
import { WorkOrderStatusSystemName } from '../entities/job/workorder.status.system.name';
import { ColorUtility } from './color.utility';

export interface HasSystemStatus {
    systemStatus: SystemWorkOrderStatus;
}

/**
 * Statuses that are set on a stage's behalf, which an operator must never pick directly.
 *
 * Subcontractor Dispatched is applied by contractor dispatch — it records that the work went out to a
 * contractor, so choosing it by hand would assert something that did not happen.
 *
 * Dispatched is applied by the Dispatch action — dispatch triggers the resource/contractor side effects
 * (resource status, emails, notifications) and then stamps the status, so picking it as a plain status
 * would claim a dispatch that never ran. The backend rejects it as a status-change target (WBS-10239).
 */
const SYSTEM_MANAGED_STATUS_SYSTEM_NAMES: readonly string[] = [
    WorkOrderStatusSystemName.SubcontractorDispatched,
    WorkOrderStatusSystemName.Dispatched,
];

/** Picker variant that narrows a status list to the ones an operator may set. */
export const USER_SELECTABLE_STATUS_PICKER_VARIANT = 'user-selectable';

export class WorkOrderStatusUtility {

    /**
     * Whether an operator may set this status directly. Keyed on `systemName` — the status's unique
     * identifier — so a tenant-authored status is selectable, while a system-managed one never is.
     */
    public static isUserSelectable(status: { systemName?: string }): boolean {

        return !SYSTEM_MANAGED_STATUS_SYSTEM_NAMES.includes(status?.systemName ?? '');
    }

    public static getColor(status: HasSystemStatus): string {
        if (!status) return 'transparent';

        return this.getSystemColor(status.systemStatus);
    }

    public static getSystemColor(status: SystemWorkOrderStatus): string {
        if (!status) return 'transparent';

        switch (status) {
            case SystemWorkOrderStatus.Open:
                return '#58c43a'; // '#ec3324';

            case SystemWorkOrderStatus.InProgress:
                return '#f29d39';

            case SystemWorkOrderStatus.OnHold:
                return '#ec3324'; // '#fffe55';

            case SystemWorkOrderStatus.Completed:
                return '#347ff7'; // '#347ff7';

            case SystemWorkOrderStatus.Finalised:
                return '#2458ac'; // '#347ff7';

            case SystemWorkOrderStatus.Closed:
                return '#fffe55'; // '#58c43a';

        }
    }

    public static getTextColor(status: WorkOrderStatus): string {
        const color = this.getColor(status);
        return ColorUtility.idealTextColor(color);
    }

    public static getSystemTextColor(status: SystemWorkOrderStatus): string {
        const color = this.getSystemColor(status);
        return ColorUtility.idealTextColor(color);
    }

    public static getSortOrder(status: HasSystemStatus): number {
        if (!status) return 0;

        switch (status.systemStatus) {
            case SystemWorkOrderStatus.Open:
                return 1000;

            case SystemWorkOrderStatus.InProgress:
                return 2000;

            case SystemWorkOrderStatus.OnHold:
                return 3000;

            case SystemWorkOrderStatus.Completed:
                return 4000;

            case SystemWorkOrderStatus.Finalised:
                return 5000;

            case SystemWorkOrderStatus.Closed:
                return 6000;

        }
    }

    public static getAll(): string[] {
        return [
            SystemWorkOrderStatus.Open,
            SystemWorkOrderStatus.InProgress,
            SystemWorkOrderStatus.OnHold,
            SystemWorkOrderStatus.Completed,
            SystemWorkOrderStatus.Finalised,
            SystemWorkOrderStatus.Closed
        ];
    }

    public static getAllSystem(excludeClosed = true): string[] {
        const items = [
            SystemWorkOrderStatus.Open,
            SystemWorkOrderStatus.InProgress,
            SystemWorkOrderStatus.OnHold,
            SystemWorkOrderStatus.Completed,
            SystemWorkOrderStatus.Finalised,
        ];

        if (!excludeClosed)
            items.push(SystemWorkOrderStatus.Closed);

        return items;
    }

    public static jobStatusCalculation(statuses: WorkOrderStatus[]): WorkOrderStatus {

        return statuses[0];
    }

    public static isCompletedOrAfter(status: WorkOrderStatus | SystemWorkOrderStatus) {

        const systemStatus = status['systemStatus']
            ? status['systemStatus'] as SystemWorkOrderStatus
            : status as SystemWorkOrderStatus;

        return Enumerable
            .from([
                SystemWorkOrderStatus.Completed,
                SystemWorkOrderStatus.Finalised,
                SystemWorkOrderStatus.Closed,
            ])
            .any(x => x === systemStatus);
    }

    public static isDispatchedOrAfter(status: WorkOrderStatus) {

        return Enumerable
            .from([
                WorkOrderStatusSystemName.Dispatched,
                WorkOrderStatusSystemName.SubcontractorDispatched,
                SystemWorkOrderStatus.InProgress,
                SystemWorkOrderStatus.Completed,
                SystemWorkOrderStatus.Finalised,
                SystemWorkOrderStatus.Closed,
            ])
            .any(x => status.systemStatus === x || status.systemName === x);
    }

    public static shouldSendEmailToTechnician(status: WorkOrderStatus) {

        return Enumerable
            .from([
                WorkOrderStatusSystemName.Dispatched,
                WorkOrderStatusSystemName.SubcontractorDispatched,
                SystemWorkOrderStatus.InProgress
            ])
            .any(x => status.systemStatus === x || status.systemName === x);
    }

    public static convertToValidStatus(status: WorkOrderStatus): WorkOrderStatus {

        if (status.systemName !== WorkOrderStatusSystemName.SubcontractorDispatched)
            return status;

        status.name = WorkOrderStatusSystemName.Dispatched;
        status.systemName = WorkOrderStatusSystemName.Dispatched;

        return status;
    }


    public static getHackFilterStatus(status: StatusFilterValue[], allStatus: StatusFilterValue[]): StatusFilterValue[] {

        if (!status?.length)
            return [];

        const dispatched = Enumerable.from(status)
            .firstOrDefault(x => x.systemName === WorkOrderStatusSystemName.Dispatched);

        if (dispatched) {
            const subcontractorDispatched = Enumerable.from(allStatus)
                .firstOrDefault(x => x.systemName === WorkOrderStatusSystemName.SubcontractorDispatched);
            status.push(subcontractorDispatched);
        }

        return status;
    }

    public static getStatusDictionary(statuses: StatusFilterValue[]) {

        const statusDictionary = Enumerable
            .from(statuses)
            .where(x => !x.isSystemStatus)
            .groupBy(x => x.systemStatus)
            .toDictionary(x => x.key(), x => x.toArray());

        return statusDictionary;
    }

    public static canBeCustomStatus(status: string): boolean {
        return status === SystemWorkOrderStatus.OnHold || status === SystemWorkOrderStatus.Completed || status === SystemWorkOrderStatus.InProgress;
    }

    public static canEditVisibility(status: string): boolean {
        return status === SystemWorkOrderStatus.OnHold || status === SystemWorkOrderStatus.InProgress;
    }

}
